import React, { useEffect, useState } from 'react';
import { fetchCompetitions, fetchSchedule, generateFile, fetchPreview, fetchOctopusStatus, pushToOctopus } from './services/api';
import Admin from './pages/Admin';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import './App.css';

export default function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState('import');
  const [index, setIndex] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [octopus, setOctopus] = useState(null); // { configured, url, desk } | null
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  // Check if already logged in
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.user) setUser(data.user);
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  // Load competitions once logged in
  useEffect(() => {
    if (!user || user.must_change_password) return;
    fetchCompetitions()
      .then(setIndex)
      .catch(() => setError('Failed to load competitions'));
    fetchOctopusStatus().then(setOctopus).catch(() => setOctopus(null));
  }, [user]);

  async function handleLogout() {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    setIndex(null);
  }

  async function handleSelectCompetition(sport, competition) {
    setSelectedSport(sport);
    setSelectedCompetition(competition);
    setScheduleData(null);
    setPreview(null);
    setError(null);
    setPushResult(null);
    setLoading(true);
    try {
      const [schedData, previewData] = await Promise.all([
        fetchSchedule(sport.id, competition.id),
        fetchPreview(sport.id, competition.id)
      ]);
      setScheduleData(schedData);
      setPreview(previewData);
    } catch {
      setError('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    if (!selectedSport || !selectedCompetition) return;
    setGenerating(true);
    setError(null);
    try {
      const blob = await generateFile(selectedSport.id, selectedCompetition.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedCompetition.displayName.replace(/\s+/g, '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to generate file');
    } finally {
      setGenerating(false);
    }
  }

  async function handlePushOctopus() {
    if (!selectedSport || !selectedCompetition) return;
    setPushing(true);
    setError(null);
    setPushResult(null);
    try {
      const result = await pushToOctopus(selectedSport.id, selectedCompetition.id);
      setPushResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setPushing(false);
    }
  }

  // ── Auth states ───────────────────────────────────────────────────────────────
  if (!authChecked) return null; // silent check

  if (!user) return <Login onLogin={setUser} />;

  if (user.must_change_password) {
    return <ChangePassword user={user} onDone={() => {
      fetch('/api/auth/me', { credentials: 'include' })
        .then(r => r.json())
        .then(data => setUser(data.user));
    }} />;
  }

  // ── Main app ──────────────────────────────────────────────────────────────────
  const canAdmin = ['admin', 'collaborator'].includes(user.role);

  return (
    <div className="app">
      <header className="header">
        <div className="header-product">Sports Import Tool</div>
        <nav className="header-nav">
          <button className={`nav-btn ${view === 'import' ? 'nav-btn--active' : ''}`} onClick={() => setView('import')}>Import</button>
          {canAdmin && (
            <button className={`nav-btn ${view === 'admin' ? 'nav-btn--active' : ''}`} onClick={() => setView('admin')}>Admin</button>
          )}
          {user.role === 'admin' && (
            <button className={`nav-btn ${view === 'users' ? 'nav-btn--active' : ''}`} onClick={() => setView('users')}>Users</button>
          )}
        </nav>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
            {user.full_name}
            <span style={{
              marginLeft: '6px', fontSize: '11px', padding: '2px 6px',
              background: 'rgba(255,255,255,0.15)', borderRadius: '4px',
              textTransform: 'uppercase', letterSpacing: '0.05em'
            }}>
              {user.role}
            </span>
          </span>
          <button
            className="nav-btn"
            onClick={handleLogout}
            style={{ fontSize: '12px' }}
          >
            Sign out
          </button>
        </div>
      </header>

      {view === 'admin' && canAdmin ? (
        <div className="page">
          <h1 className="page-title">Admin</h1>
          <p className="page-subtitle">Manage league and team configurations</p>
          <Admin />
        </div>
      ) : view === 'users' && user.role === 'admin' ? (
        <div className="page">
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage user accounts and roles</p>
          <UserManagement currentUser={user} />
        </div>
      ) : (
        <div className="page">
          <h1 className="page-title">Sports Import Tool</h1>
          <p className="page-subtitle">Generate Getty Images bulk import files from live schedule data</p>

          {index && (
            <div className="card">
              <div className="card-header">
                <span className="card-label">Sport</span>
              </div>
              <div className="card-body">
                <div className="pill-group">
                  {index.sports.map(sport => (
                    <button
                      key={sport.id}
                      className={`pill ${selectedSport?.id === sport.id ? 'pill--active' : ''}`}
                      onClick={() => {
                        setSelectedSport(sport);
                        setSelectedCompetition(null);
                        setScheduleData(null);
                        setPreview(null);
                      }}
                    >
                      {sport.displayName}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedSport && (() => {
            const active = selectedSport.competitions.filter(c => c.active);
            const grouped = active.reduce((acc, c) => {
              const key = c.country || 'Other';
              if (!acc[key]) acc[key] = [];
              acc[key].push(c);
              return acc;
            }, {});
            const sortedCountries = Object.keys(grouped).sort();
            return (
              <>
                <div className="card">
                  <div className="card-header">
                    <span className="card-label">Country</span>
                    {selectedCountry && (
                      <button
                        className="btn btn--secondary"
                        style={{fontSize: '12px', padding: '3px 10px'}}
                        onClick={() => { setSelectedCountry(null); setSelectedCompetition(null); setScheduleData(null); setPreview(null); }}
                      >
                        ← All countries
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="pill-group">
                      {sortedCountries.map(country => (
                        <button
                          key={country}
                          className={`pill ${selectedCountry === country ? 'pill--active' : ''}`}
                          onClick={() => {
                            setSelectedCountry(country);
                            setSelectedCompetition(null);
                            setScheduleData(null);
                            setPreview(null);
                          }}
                        >
                          {country}
                          <span style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.75 }}>
                            {grouped[country].length}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedCountry && (
                  <div className="card">
                    <div className="card-header">
                      <span className="card-label">{selectedCountry}</span>
                    </div>
                    <div className="card-body" style={{padding: 0}}>
                      {grouped[selectedCountry].map(competition => (
                        <div
                          key={competition.id}
                          className={`team-list-item ${selectedCompetition?.id === competition.id ? 'team-list-item--active' : ''}`}
                          onClick={() => handleSelectCompetition(selectedSport, competition)}
                          style={{cursor: 'pointer'}}
                        >
                          <span className="team-list-name" style={{fontWeight: selectedCompetition?.id === competition.id ? 700 : 400}}>
                            {competition.displayName}
                          </span>
                          <span style={{color: 'var(--color-text-secondary)', fontSize: '12px'}}>
                            {competition.gender === 'Male' ? 'Men' : 'Women'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {loading && (
            <div className="card">
              <div className="card-body">
                <p className="status-text">Loading schedule...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="card">
              <div className="card-body">
                <p className="error-text">{error}</p>
              </div>
            </div>
          )}

          {scheduleData?.createdConfigs?.length > 0 && (
            <div className="warning-card">
              <p className="warning-title">
                {scheduleData.createdConfigs.length} team config{scheduleData.createdConfigs.length > 1 ? 's' : ''} created — venue details required
              </p>
              <ul className="warning-list">
                {scheduleData.createdConfigs.map(c => (
                  <li key={c.teamId}>
                    <span className="warning-team">{c.name}</span>
                    <span className="warning-file">configs/teams/{c.filename}</span>
                  </li>
                ))}
              </ul>
              {canAdmin && (
                <button className="warning-link" onClick={() => setView('admin')}>
                  Open Admin to complete configs →
                </button>
              )}
            </div>
          )}

          {scheduleData && !loading && (
            <div className="card">
              <div className="card-header">
                <span className="card-label">Schedule</span>
              </div>
              <div className="card-body">
                <div className="summary-row">
                  <div>
                    <div className="summary-title">{selectedCompetition.displayName}</div>
                    <div className="summary-meta">
                      {scheduleData.totalGames} upcoming fixtures
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {octopus?.configured && (
                      <button className="btn" onClick={handlePushOctopus} disabled={pushing} title={`Push upcoming fixtures to ${octopus.url} (desk: ${octopus.desk})`}>
                        {pushing ? 'Pushing...' : 'Push to Octopus →'}
                      </button>
                    )}
                    <button className="btn btn--primary" onClick={handleGenerate} disabled={generating}>
                      {generating ? 'Generating...' : 'Generate import file →'}
                    </button>
                  </div>
                </div>
                {pushResult && (
                  <div className="summary-meta" style={{ marginTop: '0.5rem' }}>
                    {pushResult.total === 0
                      ? 'No upcoming fixtures to push.'
                      : `Pushed ${pushResult.total} fixtures to Octopus — ${pushResult.created} created, ${pushResult.updated} updated, ${pushResult.unchanged} unchanged` +
                        (pushResult.flagged ? `, ${pushResult.flagged} flagged for review` : '') +
                        (pushResult.errors?.length ? ` — ${pushResult.errors.length} rows rejected` : '')}
                  </div>
                )}
              </div>
            </div>
          )}

          {preview && !loading && (
            <div className="card">
              <div className="card-header">
                <span className="card-label">Preview — first {preview.preview.length} of {preview.total} rows</span>
              </div>
              <div className="preview-table-wrap">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Event Name</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Venue</th>
                      <th>City</th>
                      <th>Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row, i) => (
                      <tr key={i}>
                        <td>{row.eventName}</td>
                        <td>{row.startDate}</td>
                        <td>{row.startTime}</td>
                        <td className={!row.venueName ? 'cell--missing' : ''}>{row.venueName || '—'}</td>
                        <td className={!row.city ? 'cell--missing' : ''}>{row.city || '—'}</td>
                        <td className={!row.country ? 'cell--missing' : ''}>{row.country || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── User Management Component ─────────────────────────────────────────────────
function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', full_name: '', role: 'member', email: '' });
  const [tempPassword, setTempPassword] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUsers();
    loadResetRequests();
  }, []);

  async function loadUsers() {
    const res = await fetch('/api/auth/users', { credentials: 'include' });
    const data = await res.json();
    setUsers(data.users || []);
  }

  async function loadResetRequests() {
    const res = await fetch('/api/auth/reset-requests', { credentials: 'include' });
    const data = await res.json();
    setResetRequests(data.requests || []);
  }

  async function handleAddUser(e) {
    e.preventDefault();
    setLoading(true);
    const res = await fetch('/api/auth/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(newUser)
    });
    const data = await res.json();
    if (res.ok) {
      setTempPassword(data.tempPassword);
      setNewUser({ username: '', full_name: '', role: 'member', email: '' });
      setShowAddForm(false);
      loadUsers();
    }
    setLoading(false);
  }

  async function handleResetPassword(userId) {
    const res = await fetch(`/api/auth/users/${userId}/reset-password`, {
      method: 'POST', credentials: 'include'
    });
    const data = await res.json();
    if (res.ok) setTempPassword(data.tempPassword);
    loadUsers();
    loadResetRequests();
  }

  async function handleDeleteUser(userId) {
    if (!window.confirm('Delete this user?')) return;
    await fetch(`/api/auth/users/${userId}`, { method: 'DELETE', credentials: 'include' });
    loadUsers();
  }

  const roleColor = { admin: '#4F23B0', collaborator: '#0284c7', member: '#64748b' };

  return (
    <div>
      {tempPassword && (
        <div className="warning-card" style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
          <p className="warning-title" style={{ color: '#16a34a' }}>Temporary password generated</p>
          <p style={{ fontSize: '13px', marginTop: '4px' }}>
            Share this with the user — they must change it on first login:
            <strong style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '15px' }}>{tempPassword}</strong>
          </p>
          <button className="warning-link" onClick={() => setTempPassword(null)}>Dismiss</button>
        </div>
      )}

      {resetRequests.length > 0 && (
        <div className="warning-card">
          <p className="warning-title">Password reset requests ({resetRequests.length})</p>
          {resetRequests.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <span style={{ fontSize: '13px' }}><strong>{r.full_name}</strong> ({r.username})</span>
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{r.requested_at}</span>
              <button className="btn btn--secondary" style={{ fontSize: '12px', padding: '3px 10px' }}
                onClick={() => handleResetPassword(users.find(u => u.username === r.username)?.id)}>
                Reset password
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-label">Users ({users.length})</span>
          <button className="btn btn--primary" style={{ fontSize: '12px', padding: '4px 12px' }}
            onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? 'Cancel' : '+ Add user'}
          </button>
        </div>

        {showAddForm && (
          <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border)' }}>
            <form onSubmit={handleAddUser} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Username</label>
                <input value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Full name</label>
                <input value={newUser.full_name} onChange={e => setNewUser({...newUser, full_name: e.target.value})} required
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Email (optional)</label>
                <input value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} type="email"
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Role</label>
                <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
                  <option value="member">Member</option>
                  <option value="collaborator">Collaborator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <button type="submit" disabled={loading} className="btn btn--primary" style={{ fontSize: '13px' }}>
                  {loading ? 'Creating...' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card-body" style={{ padding: 0 }}>
          {users.map(u => (
            <div key={u.id} className="team-list-item" style={{ alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <span className="team-list-name">{u.full_name}</span>
                <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>@{u.username}</span>
                {u.must_change_password === 1 && (
                  <span style={{ marginLeft: '8px', fontSize: '11px', color: '#d97706', background: '#fef3c7', padding: '1px 6px', borderRadius: '4px' }}>
                    must change password
                  </span>
                )}
              </div>
              <span style={{
                fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                background: roleColor[u.role] + '20', color: roleColor[u.role],
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'
              }}>
                {u.role}
              </span>
              {u.id !== currentUser.id && (
                <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                  <button className="btn btn--secondary" style={{ fontSize: '11px', padding: '3px 8px' }}
                    onClick={() => handleResetPassword(u.id)}>
                    Reset pw
                  </button>
                  <button className="btn btn--secondary" style={{ fontSize: '11px', padding: '3px 8px', color: '#dc2626' }}
                    onClick={() => handleDeleteUser(u.id)}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
